// Accountability ledger — actions captured in meetings, synced globally.
//
// Same write-through pattern as notes.ts: every change writes to localStorage
// immediately and POSTs to the Worker if configured. The Worker is the source
// of truth across the ManCo; localStorage is the offline cache + write buffer.
//
// One row in the ledger = one action. The five-field shape that used to live
// on the KPI itself now lives on the action — making KPIs clean dashboard
// cards and putting the "what we're doing about it" content where it belongs:
// in a record of decisions that update over time.

import { NOTES_ENDPOINT } from "../config/endpoints";

export type ActionStatus = "doing" | "blocked" | "watch" | "done";

/**
 * What kind of ledger record this is.
 *
 *   action      a task in flight or to be done. The default.
 *   commitment  a ManCo row-level decision on a distribution chart row —
 *               "we agree Card lands at 22% earn share; Seye owns". One
 *               commitment per (kpi, category) row at most. Lives on the
 *               distribution table; carries owner, blocker, status.
 *   disagree    a recorded decision NOT to pursue this row's target.
 *               Surfaces on the row as a struck-through state with reason.
 */
export type ActionKind = "action" | "commitment" | "disagree";

export interface ActionRow {
  id: string;
  /** Plain-English title. "Negotiate liability caps at onboarding". */
  title: string;
  /** KPI ids this action impacts. ["s1"], ["s1","d1"], etc. */
  impacts: string[];
  owner: string;
  status: ActionStatus;
  /** What's in the way today. */
  blockers: string;
  /** What we're doing about it. */
  doing: string;
  /** ISO date string or empty. */
  dueBy: string;
  /** When the action was first logged. */
  createdAt: string;
  author: string;
  /** Discriminator. Defaults to "action" when missing (backwards compat). */
  kind?: ActionKind;
  synced?: boolean;
}

const STORE_KEY = "bvndle-cc:actions";

function loadLocal(): ActionRow[] {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || "[]") as ActionRow[];
  } catch {
    return [];
  }
}

function saveLocal(rows: ActionRow[]): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(rows));
  } catch {
    /* private mode */
  }
}

function newId(): string {
  return "a_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 7);
}

export interface AddActionInput {
  title: string;
  impacts: string[];
  owner: string;
  status: ActionStatus;
  blockers: string;
  doing: string;
  dueBy: string;
  author: string;
  kind?: ActionKind;
}

/** Add an action. Always persists locally; POSTs to the Worker if configured. */
export async function addAction(input: AddActionInput): Promise<ActionRow> {
  const row: ActionRow = {
    id: newId(),
    title: input.title.trim(),
    impacts: input.impacts,
    owner: input.owner.trim(),
    status: input.status,
    blockers: input.blockers.trim(),
    doing: input.doing.trim(),
    dueBy: input.dueBy,
    createdAt: new Date().toISOString(),
    author: input.author,
    kind: input.kind ?? "action",
    synced: false,
  };
  const local = loadLocal();
  local.push(row);
  saveLocal(local);

  if (NOTES_ENDPOINT) {
    try {
      const r = await fetch(`${NOTES_ENDPOINT.replace(/\/$/, "")}/actions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(row),
      });
      if (r.ok) {
        row.synced = true;
        const after = loadLocal().map((a) => (a.id === row.id ? row : a));
        saveLocal(after);
      }
    } catch {
      /* stays local; surface as unsynced */
    }
  }
  return row;
}

/** Patch an existing action (owner change, status update, etc). */
export async function updateAction(id: string, patch: Partial<ActionRow>): Promise<void> {
  const local = loadLocal();
  const idx = local.findIndex((a) => a.id === id);
  if (idx < 0) return;
  const before = local[idx]!;
  const next: ActionRow = { ...before, ...patch, id: before.id, synced: false };
  local[idx] = next;
  saveLocal(local);

  if (NOTES_ENDPOINT) {
    try {
      const r = await fetch(`${NOTES_ENDPOINT.replace(/\/$/, "")}/actions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(next),
      });
      if (r.ok) {
        next.synced = true;
        local[idx] = next;
        saveLocal(local);
      }
    } catch {
      /* stays local */
    }
  }
}

export async function deleteAction(id: string): Promise<void> {
  const local = loadLocal().filter((a) => a.id !== id);
  saveLocal(local);
  if (NOTES_ENDPOINT) {
    try {
      await fetch(`${NOTES_ENDPOINT.replace(/\/$/, "")}/actions/${id}`, { method: "DELETE" });
    } catch {
      /* localStorage already updated */
    }
  }
}

/** List all actions. Merges Worker + local, newest first. */
export async function listActions(): Promise<ActionRow[]> {
  const local = loadLocal();
  let remote: ActionRow[] = [];
  if (NOTES_ENDPOINT) {
    try {
      const r = await fetch(`${NOTES_ENDPOINT.replace(/\/$/, "")}/actions`);
      if (r.ok) {
        const data = (await r.json()) as { actions?: ActionRow[] };
        remote = (data.actions ?? []).map((a) => ({ ...a, synced: true }));
      }
    } catch {
      /* fall back to local */
    }
  }
  const byId = new Map<string, ActionRow>();
  for (const a of local) byId.set(a.id, a);
  for (const a of remote) byId.set(a.id, a);
  return Array.from(byId.values()).sort((a, b) =>
    a.createdAt < b.createdAt ? 1 : -1,
  );
}

// ---------------- impact path matching ------------------------------------
//
// Action impacts are hierarchical colon-separated paths. Examples:
//   "s1"                  whole KPI
//   "s1:fuel"             a category within S1
//   "s1:fuel:shell"       a specific vendor in Fuel within S1
//   "sym"                 the symmetry roll-up (touches both sides)
//
// `actionTouchesPath` is true if any of the action's impacts is the focus
// path, an ancestor of it (less specific — KPI-level action touches the
// vendor by virtue of touching the whole KPI), or a descendant (more
// specific — a vendor-level action is part of the category's story).
//
// `actionInSideScope` is true if any impact's first segment matches the
// side's KPI codes (s1/s2/s3 for supply, d1/d2/d3 for demand) or is "sym".

export function actionTouchesPath(action: ActionRow, focusPath: string): boolean {
  return action.impacts.some(
    (i) => i === focusPath || i.startsWith(focusPath + ":") || focusPath.startsWith(i + ":"),
  );
}

export function actionInSideScope(action: ActionRow, side: "supply" | "demand"): boolean {
  return action.impacts.some((i) => {
    const root = i.split(":")[0] ?? "";
    if (root === "sym") return true;
    if (side === "supply") return root === "s1" || root === "s2" || root === "s3";
    return root === "d1" || root === "d2" || root === "d3";
  });
}

export function impactsForKpi(impacts: string[], kpiId: string): string[] {
  return impacts.filter((i) => i === kpiId || i.startsWith(kpiId + ":"));
}

/**
 * Find the row commitment (if any) for a given (kpi, category) row in the
 * distribution table. There's at most one per row — if multiple exist
 * (concurrent edits), the most recent wins.
 */
export function findRowCommitment(
  actions: ActionRow[],
  kpiId: string,
  categoryCode: string,
): ActionRow | undefined {
  const focusPath = `${kpiId}:${categoryCode}`;
  return actions
    .filter(
      (a) =>
        (a.kind === "commitment" || a.kind === "disagree") &&
        a.impacts.includes(focusPath),
    )
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))[0];
}

export const STATUS_LABEL: Record<ActionStatus, string> = {
  doing: "DOING",
  blocked: "BLOCKED",
  watch: "WATCH",
  done: "DONE",
};

export const STATUS_COLOR: Record<ActionStatus, string> = {
  doing: "var(--purple)",
  blocked: "var(--coral)",
  watch: "var(--amber)",
  done: "#5ec98c",
};
